/**
 * Funding Countdown Component
 *
 * Displays countdown timer to next funding event for Binance Futures.
 * Funding occurs every 8 hours at 00:00, 08:00, 16:00 UTC.
 */

import React, { useState, useEffect } from 'react';
import { Typography } from '@mui/material';

/**
 * Props for FundingCountdown component
 */
interface FundingCountdownProps {
  /** UTC timestamp of next funding time in milliseconds (from Epic 8) */
  nextFundingTime: number | null;
  /** Rounded funding interval in hours for symbol-specific countdown rollover */
  fundingIntervalHours?: number | null;
}

const DEFAULT_FUNDING_INTERVAL_HOURS = 8;

function resolveEffectiveFundingTime(
  nextFundingTime: number | null,
  fundingIntervalHours: number | null | undefined,
  now: number
): number | null {
  if (nextFundingTime === null || nextFundingTime <= 0) {
    return null;
  }

  if (nextFundingTime > now) {
    return nextFundingTime;
  }

  const intervalHours = fundingIntervalHours ?? DEFAULT_FUNDING_INTERVAL_HOURS;
  if (intervalHours <= 0) {
    return nextFundingTime;
  }

  const intervalMs = intervalHours * 60 * 60 * 1000;
  const elapsedSinceFunding = now - nextFundingTime;
  const elapsedIntervals = Math.floor(elapsedSinceFunding / intervalMs) + 1;

  return nextFundingTime + elapsedIntervals * intervalMs;
}

/**
 * Custom hook for managing countdown timer
 *
 * @param nextFundingTime - UTC timestamp of next funding event (milliseconds)
 * @param fundingIntervalHours - Optional rounded funding interval in hours
 * @returns Formatted countdown string ("HH:MM:SS" or "--:--:--" if null)
 */
function useFundingCountdown(
  nextFundingTime: number | null,
  fundingIntervalHours?: number | null
): string {
  const [countdown, setCountdown] = useState<string>('--:--:--');

  useEffect(() => {
    // Handle null case - no funding data available yet
    if (nextFundingTime === null) {
      setCountdown('--:--:--');
      return;
    }

    /**
     * Format milliseconds to HH:MM:SS string
     *
     * @param ms - Time remaining in milliseconds
     * @returns Formatted time string with zero-padding
     */
    const formatTime = (ms: number): string => {
      // Handle negative or zero time (countdown expired)
      if (ms <= 0) {
        return '00:00:00';
      }

      const totalSeconds = Math.floor(ms / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    /**
     * Update countdown every second
     * Uses the symbol-specific nextFundingTime from props
     */
    const updateCountdown = () => {
      const now = Date.now();
      const effectiveFundingTime = resolveEffectiveFundingTime(
        nextFundingTime,
        fundingIntervalHours,
        now
      );

      if (effectiveFundingTime === null) {
        setCountdown('--:--:--');
        return;
      }

      const timeRemaining = effectiveFundingTime - now;
      setCountdown(formatTime(timeRemaining));
    };

    // Initial update
    updateCountdown();

    // Set up 1-second interval
    const intervalId = setInterval(updateCountdown, 1000);

    // Cleanup interval on unmount or when nextFundingTime changes
    return () => {
      clearInterval(intervalId);
    };
  }, [nextFundingTime, fundingIntervalHours]);

  return countdown;
}

/**
 * Funding Countdown Display Component
 *
 * Shows countdown timer to next Binance Futures funding event.
 * Updates every second and automatically resets to next 8-hour cycle when countdown expires.
 *
 * @param props - Component props
 * @returns Countdown timer display
 */
export const FundingCountdown: React.FC<FundingCountdownProps> = ({
  nextFundingTime,
  fundingIntervalHours,
}) => {
  const countdown = useFundingCountdown(nextFundingTime, fundingIntervalHours);

  return (
    <Typography
      variant="body2"
      component="span"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        color: nextFundingTime === null ? 'text.disabled' : 'text.secondary',
      }}
    >
      {countdown}
    </Typography>
  );
};

export default FundingCountdown;
